/**
 * PLPMobileSwipe - Mobile swipe-through-images on PLP product cards
 * Allows users to swipe left/right on product cards to browse images
 *
 * The breakpoint lives here and nowhere else: cards are built when the media
 * query starts matching and torn down when it stops, so resizing or rotating
 * across it leaves neither a broken grid nor a dead swipe area.
 */
window.PLPMobileSwipe = {
  initialized: new WeakSet(),
  hintPlayed: false,
  _userInteracted: false,
  _cancelHint: null,
  _preloadObserver: null,

  // Touch thresholds
  SWIPE_THRESHOLD: 40,
  VELOCITY_THRESHOLD: 0.3,
  AXIS_LOCK_DISTANCE: 10,
  // How much more horizontal than vertical a gesture must be before we take it
  // over. A bare majority claims near-diagonal scrolls, which reads as the card
  // image sliding sideways under the thumb.
  AXIS_LOCK_RATIO: 1.5,

  // Fallback src only - the srcset in buildSlide carries the real candidates and
  // is what the browser picks from whenever window.OAImageSrcSet is available.
  IMAGE_SIZE: '350x',

  // The collection PLP sits outside the A/B test and always swipes.
  PLP_CARD_SELECTOR: '.oa-shop-grid__item[data-product-handle]',

  // The two surfaces the A/B test gates. Container-scoped because Nosto,
  // sidecart-CAB and ls-product-grid cards share their `.oa-prdct20-item` class.
  SEARCH_CARD_SELECTOR: '#search-lp-grid .oa-prdct20-item[data-product-handle], ' +
    '.oa-hdr20__search-result-wrapper .oa-prdct20-item[data-product-handle]',

  OVERLAY_SELECTOR: '.oa-hdr20__search-result-wrapper',

  // Every card this script can own, whichever version is active - teardown and
  // external callers still have to find cards built before a version switch.
  get CARD_SELECTOR() {
    return this.PLP_CARD_SELECTOR + ', ' + this.SEARCH_CARD_SELECTOR;
  },

  // Whether version b of the search-swipe test is in effect.
  _searchSwipeOn: false,

  mq: window.matchMedia('(max-width: 1024px)'),

  // Read live rather than cached once: devtools device emulation toggles this
  // without a reload.
  hasTouch: function() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  },

  isEnabled: function() {
    return this.mq.matches && this.hasTouch();
  },

  // window.abtest.searchSwipeVersion is the live value - it carries an assignment
  // the A/B tool made before we existed, which re-reading storage would drop.
  readSearchSwipeVersion: function() {
    return (window.abtest && window.abtest.searchSwipeVersion) || 'a';
  },

  // What may be built right now, given the version in effect
  activeSelector: function() {
    return this._searchSwipeOn ? this.CARD_SELECTOR : this.PLP_CARD_SELECTOR;
  },

  init: function() {
    var self = this;
    var onChange = function() { self.onBreakpointChange(); };

    this._searchSwipeOn = this.readSearchSwipeVersion() === 'b';

    // The A/B tool can assign a version long after the cards have rendered, so
    // the search surfaces are built or torn down on its call, not only here.
    document.addEventListener('oa:searchswipeversion', function(e) {
      self.onSearchSwipeVersionChange(e && e.detail ? e.detail.version : null);
    });

    if (this.mq.addEventListener) {
      this.mq.addEventListener('change', onChange);
    } else {
      this.mq.addListener(onChange); // Safari < 14
    }

    // Any touch anywhere on the page counts as interaction. The per-card
    // touchstart handler only fires when a finger lands on a product image, so
    // a scroll begun on the background, a title or the gap between cards used
    // to leave _userInteracted false - and the hint would then animate the
    // first visible card while the user was already scrolling.
    var noteInteraction = function() { self.noteInteraction(); };
    document.addEventListener('touchstart', noteInteraction, { passive: true, capture: true });
    document.addEventListener('wheel', noteInteraction, { passive: true, capture: true });

    // Catch-up pass: this script is deferred, so a warm Algolia/CDN cache can
    // resolve hoverPromise - and run its processCards call - before we exist.
    // Without this the listener above is the only entry point, and on a phone
    // it never fires because the viewport never crosses the breakpoint.
    // Cards lacking cached images are skipped without being marked initialised,
    // so the later Algolia call still picks them up.
    this.onBreakpointChange();
  },

  /**
   * Record that the user is doing something, and abort any hint in flight
   */
  noteInteraction: function() {
    this._userInteracted = true;
    if (this._cancelHint) this._cancelHint();
  },

  /**
   * Build or tear down every card when the viewport crosses the breakpoint
   */
  onBreakpointChange: function() {
    if (this.isEnabled()) {
      this.processCards(document.querySelectorAll(this.activeSelector()));
    } else {
      this.destroyAll();
    }
  },

  /**
   * Build or tear down the search cards when the A/B tool assigns a version
   */
  onSearchSwipeVersionChange: function(version) {
    var on = (version || this.readSearchSwipeVersion()) === 'b';
    if (on === this._searchSwipeOn) return;
    this._searchSwipeOn = on;

    if (on) {
      this.processCards(document.querySelectorAll(this.SEARCH_CARD_SELECTOR));
    } else {
      this.destroySearchCards();
    }
  },

  /**
   * Process product cards and attach swipe functionality
   * Called after Algolia renders products and hover images are fetched, and by
   * each Vue product card once it has rendered
   */
  processCards: function(items) {
    if (!this.isEnabled()) return;

    var self = this;
    var selector = this.activeSelector();
    var newCards = [];
    Array.prototype.forEach.call(items, function(card) {
      if (self.initialized.has(card)) return;

      // A Vue card hands us its own root element, so don't trust the caller -
      // and this is where version a of the A/B test drops the search cards.
      if (!card.matches(selector)) return;

      var handle = card.getAttribute('data-product-handle');
      if (!handle) return;

      var productId = card.getAttribute('data-product-id');
      if (!productId) return;

      var images = self.getSwipeImages(productId, card);

      // Need at least 2 images for swiping
      if (images.length < 2) return;

      // Cap at 4 images
      images = images.slice(0, 4);

      if (!self.buildSwipeDOM(card, images)) return;
      self.attachTouchHandlers(card);
      self.initialized.add(card);
      newCards.push(card);
    });

    if (newCards.length) this.observePreload(newCards);

    // Play swipe hint on the first visible card (once per page load). A call that
    // built nothing must not spend the flag, and the dropdown never hints - it
    // opens mid-typing, over the keyboard.
    var hintable = newCards.filter(function(card) {
      return !card.closest(self.OVERLAY_SELECTOR);
    });
    if (hintable.length && !this.hintPlayed) {
      this.hintPlayed = true;
      setTimeout(function() {
        if (self._userInteracted) return;
        // Query fresh DOM elements (Algolia may have re-rendered since processCards)
        var freshCards = Array.prototype.slice.call(document.querySelectorAll('[data-product-id]'));
        self.playSwipeHint(freshCards);
      }, 1500);
    }
  },

  /**
   * Slide image URLs from the hover-image cache - one cache for every surface,
   * filled by AlgoliaMediaManager on the PLP and through VueHoverImageManager
   * on search.
   */
  getSwipeImages: function(productId, card) {
    if (typeof AlgoliaMediaManager === 'undefined') return [];
    var entry = AlgoliaMediaManager.hoverImageCache.get(String(productId));
    if (!entry || !entry.allImages) return [];

    var images = entry.allImages.slice();
    // Slide 1 is the <picture> the card already rendered. Only the PLP swaps a
    // lifestyle-first card's main picture, and it swaps in the hover image -
    // which is images[3], not images[1], for a video or hoverimg4 product.
    if (entry.isLifestyleFirst && card && card.matches(this.PLP_CARD_SELECTOR)) {
      var rendered = images.indexOf(entry.hoverUrl);
      if (rendered > 0) images.unshift(images.splice(rendered, 1)[0]);
    }
    return images;
  },

  /**
   * Build swipe DOM structure inside the product card image wrapper
   */
  buildSwipeDOM: function(card, imageUrls) {
    var wrapper = card.querySelector('.oa-prdct20-fashion__image-wrapper');
    if (!wrapper) return false;

    // Mark as swipe-active
    var imageContainer = card.querySelector('.oa-prdct20-fashion__image-container');
    if (imageContainer) {
      imageContainer.classList.add('plp-swipe-active');
    }

    // Real slides plus a clone at each end for the wrap-around loop
    var trackSlides = imageUrls.length + 2;

    // Get existing picture element (first image)
    var existingPicture = wrapper.querySelector('picture');
    var existingImg = existingPicture && existingPicture.querySelector('img');
    var slideAlt = existingImg ? existingImg.alt : '';

    // Create swipe track (real slides + 2 clones). Track and slide widths are
    // derived from --plp-swipe-slides in CSS rather than measured here, so they
    // track the wrapper through any resize or rotation.
    var track = document.createElement('div');
    track.className = 'plp-swipe-track';
    track.style.setProperty('--plp-swipe-slides', String(trackSlides));

    // Clone of last image (track index 0 - for backward loop)
    track.appendChild(this.buildSlide(imageUrls[imageUrls.length - 1], slideAlt));

    // First real slide (track index 1): wrap existing picture
    var firstSlide = document.createElement('div');
    firstSlide.className = 'plp-swipe-slide';
    if (existingPicture) {
      firstSlide.appendChild(existingPicture);
    }
    // A card video (plp-thumb-video.js) covers the picture, so it moves into slide 1 with it.
    var thumbVideo = wrapper.querySelector('.plp-thumb-video');
    if (thumbVideo) {
      firstSlide.appendChild(thumbVideo);
    }
    track.appendChild(firstSlide);

    // Additional slides: simple img tags with data-src for lazy loading
    for (var i = 1; i < imageUrls.length; i++) {
      track.appendChild(this.buildSlide(imageUrls[i], slideAlt));
    }

    // Clone of first image (for forward loop)
    track.appendChild(this.buildSlide(imageUrls[0], slideAlt));

    // Create pagination dots
    var dots = document.createElement('div');
    dots.className = 'plp-swipe-dots';
    for (var j = 0; j < imageUrls.length; j++) {
      var dot = document.createElement('span');
      dot.className = 'plp-swipe-dot' + (j === 0 ? ' plp-swipe-dot--active' : '');
      dots.appendChild(dot);
    }

    // Insert into wrapper
    wrapper.appendChild(track);
    wrapper.appendChild(dots);

    // Store state on the card
    card._swipe = {
      track: track,
      dots: dots,
      wrapper: wrapper,
      container: imageContainer,
      currentIndex: 0,
      visualIndex: 1,
      totalSlides: imageUrls.length,
      trackSlides: trackSlides,
      animating: false,
      handlers: null
    };

    // Show slide 1 immediately, skipping clone-last at track index 0
    this.setRestingTransform(card._swipe);
    this.syncThumbVideo(card);
    return true;
  },

  // The card video only shows on slide 1; PLPThumbVideo pauses it elsewhere.
  syncThumbVideo: function(card) {
    var swipe = card._swipe;
    if (!swipe || !window.PLPThumbVideo) return;
    var video = swipe.track.querySelector('.plp-thumb-video');
    if (!video) return;
    window.PLPThumbVideo.setHidden(video, swipe.currentIndex !== 0);
  },

  /**
   * Build a single lazily-loaded slide
   */
  buildSlide: function(imageUrl, alt) {
    var slide = document.createElement('div');
    slide.className = 'plp-swipe-slide';
    var img = document.createElement('img');
    img.className = 'plp-swipe-img';
    // A slide fills the wrapper, so it sits in the same slot as the main/hover picture.
    if (typeof window.OAImageSrcSet === 'function' && window.OA_COLLECTION_ITEM_IMAGE_SIZES) {
      img.setAttribute('data-srcset', window.OAImageSrcSet(imageUrl));
      img.setAttribute('sizes', window.OA_COLLECTION_ITEM_IMAGE_SIZES);
    }
    img.setAttribute('data-src', this.getSizedUrl(imageUrl, this.IMAGE_SIZE));
    // Alt for crawlers; aria-hidden because slide 1 already names the card link.
    img.alt = alt || '';
    img.setAttribute('aria-hidden', 'true');
    slide.appendChild(img);
    return slide;
  },

  /**
   * Promote a slide's lazy attributes. Kept in one place so srcset and src stay in step.
   */
  loadSlideImage: function(img, onLoad, priority) {
    if (!img) {
      if (onLoad) onLoad();
      return;
    }

    var settle = function() {
      img.classList.add('plp-swipe-img--loaded');
      if (onLoad) onLoad();
    };
    // A 404 or an aborted fetch must still release whatever is chained behind this slide,
    // otherwise the backward warm-up and the hint animation wait forever.
    var release = function() { if (onLoad) onLoad(); };

    var whenSettled = function() {
      if (img.complete) settle();
      else {
        img.addEventListener('load', settle, { once: true });
        img.addEventListener('error', release, { once: true });
      }
    };

    // Already promoted, by a warm preload or an earlier gesture. Nothing left to fetch, but a
    // caller waiting on the paint still has to be answered.
    if (img.src) {
      whenSettled();
      return;
    }

    if (priority) img.fetchPriority = priority;
    var srcset = img.getAttribute('data-srcset');
    if (srcset) {
      img.srcset = srcset;
      img.removeAttribute('data-srcset');
    }
    img.src = img.getAttribute('data-src');
    img.removeAttribute('data-src');
    // A cached image is already complete here, and the 0.2s fade-in would then be
    // pure added latency in front of a picture the browser could paint right away.
    whenSettled();
  },

  /**
   * Write the canonical resting position for the current slide.
   * A percentage translate resolves against the track's own width, so this
   * stays correct at any viewport width without re-measuring.
   */
  setRestingTransform: function(swipe) {
    var pct = -(swipe.visualIndex * 100 / swipe.trackSlides);
    swipe.track.style.transform = 'translateX(' + pct + '%)';
  },

  /**
   * Remove the swipe layer and restore the card to its rendered shape
   */
  destroyCard: function(card) {
    var swipe = card._swipe;
    if (!swipe) return;

    if (swipe.handlers) {
      swipe.wrapper.removeEventListener('touchstart', swipe.handlers.touchstart);
      swipe.wrapper.removeEventListener('touchmove', swipe.handlers.touchmove);
      swipe.wrapper.removeEventListener('touchend', swipe.handlers.touchend);
      swipe.wrapper.removeEventListener('touchcancel', swipe.handlers.touchcancel);
      swipe.wrapper.removeEventListener('click', swipe.handlers.click, true);
    }

    // Put the main <picture> back where the card was rendered with it: after
    // the canvas that gives the wrapper its aspect ratio, and ahead of the
    // --isoverlay hover picture that desktop hover-swap fades in.
    var picture = swipe.track.querySelector('picture');
    if (picture) {
      var canvas = swipe.wrapper.querySelector('.oa-picture__canvas');
      if (canvas) {
        swipe.wrapper.insertBefore(picture, canvas.nextSibling);
      } else {
        swipe.wrapper.insertBefore(picture, swipe.wrapper.firstChild);
      }
    }

    // The card video goes back next to the picture, or it leaves with the track.
    var thumbVideo = swipe.track.querySelector('.plp-thumb-video');
    if (thumbVideo) {
      if (picture && picture.parentNode) {
        picture.parentNode.insertBefore(thumbVideo, picture.nextSibling);
      } else {
        swipe.wrapper.appendChild(thumbVideo);
      }
      if (window.PLPThumbVideo) window.PLPThumbVideo.setHidden(thumbVideo, false);
    }

    if (swipe.track.parentNode) swipe.track.parentNode.removeChild(swipe.track);
    if (swipe.dots.parentNode) swipe.dots.parentNode.removeChild(swipe.dots);
    if (swipe.container) swipe.container.classList.remove('plp-swipe-active');

    if (this._preloadObserver) this._preloadObserver.unobserve(card);
    this.initialized.delete(card);
    delete card._swipe;
  },

  /**
   * Tear down every built card - used when the viewport leaves the breakpoint
   */
  destroyAll: function() {
    var self = this;
    var cards = document.querySelectorAll(this.CARD_SELECTOR);
    Array.prototype.forEach.call(cards, function(card) {
      self.destroyCard(card);
    });
  },

  /**
   * Tear down the search cards only - used when the A/B test switches to a
   */
  destroySearchCards: function() {
    var self = this;
    var cards = document.querySelectorAll(this.SEARCH_CARD_SELECTOR);
    Array.prototype.forEach.call(cards, function(card) {
      self.destroyCard(card);
    });
  },

  /**
   * Attach touch event handlers to a product card
   */
  attachTouchHandlers: function(card) {
    var self = this;
    var swipe = card._swipe;
    if (!swipe) return;
    var wrapper = swipe.wrapper;

    var startX = 0;
    var startY = 0;
    var startTime = 0;
    var currentX = 0;
    var isHorizontal = null;
    var didSwipe = false;
    var containerWidth = 0;
    var hideTimeout = null;

    function onTouchStart(e) {
      if (swipe.animating) return;

      self._userInteracted = true;

      // Preload adjacent slides immediately so they're ready before the drag reveals them
      self.lazyLoadSlide(swipe.track, swipe.currentIndex);

      var touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      startTime = Date.now();
      currentX = 0;
      isHorizontal = null;
      didSwipe = false;
      // Measured per gesture, so a resize between gestures needs no bookkeeping
      containerWidth = wrapper.offsetWidth;
    }

    function onTouchMove(e) {
      if (swipe.animating) return;
      if (isHorizontal === false) return;

      var touch = e.touches[0];
      var deltaX = touch.clientX - startX;
      var deltaY = touch.clientY - startY;

      // Determine axis lock.
      //
      // Vertical wins ties and near-diagonals: mistakenly claiming a scroll is
      // far worse than missing a swipe, because preventDefault then fights the
      // browser's own scrolling (and is ignored outright once the browser has
      // committed, which is what logs the touchmove intervention). Staying
      // undecided is safe - we neither drag the track nor block the scroll.
      if (isHorizontal === null) {
        var absX = Math.abs(deltaX);
        var absY = Math.abs(deltaY);

        if (absY > self.AXIS_LOCK_DISTANCE && absY >= absX) {
          isHorizontal = false;
          return;
        }
        if (absX > self.AXIS_LOCK_DISTANCE && absX > absY * self.AXIS_LOCK_RATIO) {
          isHorizontal = true;
        } else {
          return; // still ambiguous - wait for more travel
        }
      }

      // The axis lock needs AXIS_LOCK_DISTANCE of travel before it can decide,
      // and we can't preventDefault before then without breaking vertical
      // scrolling. In that window the browser may commit to a scroll of its own
      // (touch-action: pan-y permits it), after which touchmove arrives with
      // cancelable:false, preventDefault() is ignored, and Chrome logs an
      // intervention. Skip the call we know will be ignored; the gesture is
      // otherwise handled exactly as before.
      if (e.cancelable) e.preventDefault();
      didSwipe = true;
      currentX = deltaX;

      // Pixels for the duration of the drag only; touchend re-anchors to the
      // percentage-based resting position.
      var baseOffset = -swipe.visualIndex * containerWidth;
      swipe.track.style.transform = 'translateX(' + (baseOffset + deltaX) + 'px)';
    }

    function onTouchEnd() {
      if (isHorizontal !== true) return;
      if (swipe.animating) return;

      var elapsed = Date.now() - startTime;
      var velocity = Math.abs(currentX) / elapsed;
      var newVisualIndex = swipe.visualIndex;

      if (Math.abs(currentX) > self.SWIPE_THRESHOLD || velocity > self.VELOCITY_THRESHOLD) {
        if (currentX < 0) {
          newVisualIndex = swipe.visualIndex + 1;
        } else if (currentX > 0) {
          newVisualIndex = swipe.visualIndex - 1;
        }
      }

      // Map visual index to logical index for dots
      var logicalIndex = newVisualIndex - 1;
      if (logicalIndex < 0) logicalIndex = swipe.totalSlides - 1;
      if (logicalIndex >= swipe.totalSlides) logicalIndex = 0;

      // Lazy load the target slide image
      self.lazyLoadSlide(swipe.track, logicalIndex);

      swipe.visualIndex = newVisualIndex;
      swipe.currentIndex = logicalIndex;
      self.updateDots(card);
      // At gesture end, not after the animation: the video is already sliding out.
      self.syncThumbVideo(card);
      self.animateSlide(swipe, -newVisualIndex * containerWidth, function() {
        // After animating onto a clone, silently jump to its real counterpart.
        // animateSlide re-applies the resting transform once this returns.
        if (newVisualIndex === 0) {
          swipe.visualIndex = swipe.totalSlides;
        } else if (newVisualIndex === swipe.totalSlides + 1) {
          swipe.visualIndex = 1;
        }
      });

      // Show progress bars on swipe, auto-hide after 2s
      swipe.dots.classList.add('plp-swipe-dots--visible');
      if (hideTimeout) clearTimeout(hideTimeout);
      hideTimeout = setTimeout(function() {
        swipe.dots.classList.remove('plp-swipe-dots--visible');
      }, 2000);
    }

    // A cancelled gesture (iOS edge-swipe, incoming call, scroll takeover) never
    // fires touchend, so nothing would re-anchor the track - it would sit at the
    // raw mid-drag pixel offset showing two half images until the next swipe.
    function onTouchCancel() {
      isHorizontal = null;
      didSwipe = false;
      currentX = 0;
      if (!swipe.animating) self.setRestingTransform(swipe);
    }

    // Prevent link navigation when swiping
    function onClick(e) {
      if (didSwipe) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    wrapper.addEventListener('touchstart', onTouchStart, { passive: true });
    wrapper.addEventListener('touchmove', onTouchMove, { passive: false });
    wrapper.addEventListener('touchend', onTouchEnd, { passive: true });
    wrapper.addEventListener('touchcancel', onTouchCancel, { passive: true });
    wrapper.addEventListener('click', onClick, true);

    // Kept so teardown can detach them: the wrapper <a> outlives the swipe DOM
    swipe.handlers = {
      touchstart: onTouchStart,
      touchmove: onTouchMove,
      touchend: onTouchEnd,
      touchcancel: onTouchCancel,
      click: onClick
    };
  },

  /**
   * Animate slide transition using CSS transition
   */
  animateSlide: function(swipe, toX, onComplete) {
    var self = this;
    var track = swipe.track;
    swipe.animating = true;
    var finished = false;

    function finish() {
      if (finished) return;
      finished = true;
      track.removeEventListener('transitionend', onEnd);
      track.style.transition = '';
      swipe.animating = false;
      if (onComplete) onComplete();
      // The animation ran in pixels and onComplete may have jumped a clone to
      // its real counterpart, so settle on the percentage position either way.
      self.setRestingTransform(swipe);
    }

    function onEnd() { finish(); }

    track.style.transition = 'transform 250ms ease-out';
    track.style.transform = 'translateX(' + toX + 'px)';
    track.addEventListener('transitionend', onEnd);

    // Fallback in case transitionend doesn't fire
    setTimeout(finish, 300);
  },

  /**
   * Update pagination dots to reflect current slide
   */
  updateDots: function(card) {
    var swipe = card._swipe;
    if (!swipe || !swipe.dots) return;

    var dots = swipe.dots.children;
    for (var i = 0; i < dots.length; i++) {
      if (i === swipe.currentIndex) {
        dots[i].classList.add('plp-swipe-dot--active');
      } else {
        dots[i].classList.remove('plp-swipe-dot--active');
      }
    }
  },

  /**
   * Lazy load an image in a specific slide
   */
  lazyLoadSlide: function(track, index) {
    // Load target slide + adjacent slides (next and previous) to cover both swipe directions
    for (var i = index - 1; i <= index + 1; i++) {
      if (i < 0) continue;
      var slide = track.children[i + 1];
      if (!slide) continue;

      // High priority: a gesture is already waiting on these, unlike the warm preload.
      this.loadSlideImage(slide.querySelector('img[data-src]'), null, 'high');
    }

    // Load clone-last (track index 0) when on the first real slide - shown on backward loop
    if (index === 0) {
      var cloneLast = track.children[0];
      if (cloneLast) {
        this.loadSlideImage(cloneLast.querySelector('img[data-src]'), null, 'high');
      }
    }

    // Load clone-first (last track child) when on the last real slide - shown on forward loop
    if (index === track.children.length - 3) {
      var cloneFirst = track.children[track.children.length - 1];
      if (cloneFirst) {
        this.loadSlideImage(cloneFirst.querySelector('img[data-src]'), null, 'high');
      }
    }
  },

  /**
   * Play a subtle swipe hint on the first visible product card
   */
  playSwipeHint: function(items) {
    var self = this;
    // Re-check here as well as at the call site: the 1500ms timer means the
    // user may have started scrolling in the meantime.
    if (this._userInteracted) return;

    // Find the first card that is in the viewport, skipping the search dropdown
    var card = null;
    for (var i = 0; i < items.length; i++) {
      if (!items[i]._swipe) continue;
      if (items[i].closest(self.OVERLAY_SELECTOR)) continue;
      var rect = items[i].getBoundingClientRect();
      if (rect.top >= 0 && rect.top < window.innerHeight) {
        card = items[i];
        break;
      }
    }
    if (!card) return;

    var swipe = card._swipe;
    var track = swipe.track;
    var wrapper = card.querySelector('.oa-prdct20-fashion__image-wrapper');
    // Same measurement the working touch handlers use (line 179)
    var slideWidth = wrapper ? wrapper.offsetWidth : 0;
    if (!slideWidth) return;
    var dragPx = Math.round(slideWidth * 0.45);

    var hintCancelled = false;

    function cancelHint() {
      hintCancelled = true;
      self._cancelHint = null;
      // The card may have been torn down at the breakpoint since the hint was
      // scheduled, leaving this closure holding a detached track.
      if (!track.isConnected) return;
      track.style.transition = '';
      self.setRestingTransform(swipe);
      swipe.dots.classList.remove('plp-swipe-dots--visible');
    }

    // Reachable from noteInteraction(), so a touch anywhere aborts the hint -
    // not just a touch that lands on this card.
    self._cancelHint = cancelHint;

    function startHintAnimation() {
      if (hintCancelled || !track.isConnected) return;

      // Show progress bars during hint
      swipe.dots.classList.add('plp-swipe-dots--visible');

      // 650ms drag up
      track.style.transition = 'transform 650ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      track.style.transform = 'translateX(-' + (slideWidth + dragPx) + 'px)';

      // 250ms hold, then 650ms spring back to first real slide
      setTimeout(function() {
        if (hintCancelled || !track.isConnected) return;
        track.style.transition = 'transform 650ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        track.style.transform = 'translateX(-' + slideWidth + 'px)';

        setTimeout(function() {
          if (hintCancelled || !track.isConnected) return;
          track.style.transition = '';
          self.setRestingTransform(swipe);
          self._cancelHint = null; // hint finished on its own

          setTimeout(function() {
            swipe.dots.classList.remove('plp-swipe-dots--visible');
          }, 1000);
        }, 650);
      }, 900);
    }

    // Hold the hint until the slide it drags into view has painted. Matched on the element,
    // not [data-src], because a warm preload may have promoted it with the fetch still open.
    var slide2 = track.children[2];
    var img = slide2 ? slide2.querySelector('img') : null;
    if (img && !(img.src && img.complete)) {
      self.loadSlideImage(img, startHintAnimation, 'high');
      // Skip hint if image takes too long
      setTimeout(function() {
        if (!img.complete) hintCancelled = true;
      }, 5000);
    } else {
      startHintAnimation();
    }
  },

  /**
   * Metered or 2G connections still get the slide on touchstart, they just don't pay up front.
   */
  connectionAllowsPreload: function() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return true;
    if (c.saveData) return false;
    return !/(^|-)2g$/.test(c.effectiveType || '');
  },

  /**
   * Warm the slides a first gesture can reveal, starting before the card is on screen so the
   * fetch is already in flight when the user swipes the card they scrolled to.
   */
  observePreload: function(cards) {
    var self = this;

    if (!('IntersectionObserver' in window)) {
      // No observer to tell us what is on screen, so warm the head of the grid instead.
      cards.slice(0, 10).forEach(function(card) {
        self.whenIdle(function() { self.warmAdjacentSlides(card); });
      });
      return;
    }

    if (!this._preloadObserver) {
      this._preloadObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          self._preloadObserver.unobserve(entry.target);
          self.warmAdjacentSlides(entry.target);
        });
      }, { rootMargin: '200px' });
    }

    cards.forEach(function(card) {
      if (card._swipe) self._preloadObserver.observe(card);
    });
  },

  whenIdle: function(fn) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 2000 });
    } else {
      setTimeout(fn, 300);
    }
  },

  warmAdjacentSlides: function(card) {
    // Checked here rather than when the observer is built, so every card is judged
    // against the connection it is actually warmed on.
    if (!card._swipe || !this.connectionAllowsPreload()) return;
    var self = this;
    var track = card._swipe.track;

    // At rest the track shows children[1]. Either neighbour can be revealed first - swiping
    // back wraps around to clone-last.
    var forward = track.children[2];
    var backward = track.children[0];

    var warmBackward = function() {
      if (!card._swipe || !backward) return;
      self.whenIdle(function() {
        if (!card._swipe) return;
        // Speculative, so it stays out of the way. On a two-image product this is the same
        // URL as the forward slide and resolves from cache anyway.
        self.loadSlideImage(backward.querySelector('img[data-src]'), null, 'low');
      });
    };

    // Chained so the forward slide - the one most gestures reveal first - has the bandwidth
    // to itself. Default priority, since an in-flight fetch cannot be promoted on swipe.
    this.loadSlideImage(forward ? forward.querySelector('img[data-src]') : null, warmBackward);
  },

  /**
   * Add size suffix to Shopify CDN image URL, via the shared helper so the fallback src
   * and the srcset candidates resolve to the same CDN cache entries.
   */
  getSizedUrl: function(url, size) {
    if (!url || !size || typeof window.OAImageSrc !== 'function') return url;
    return window.OAImageSrc(url, size);
  },

  /**
   * Manually trigger swipe hint from console: PLPMobileSwipe.debug()
   */
  debug: function() {
    this.hintPlayed = false;
    this._userInteracted = false; // otherwise the guard in playSwipeHint blocks it
    var cards = document.querySelectorAll(this.CARD_SELECTOR);
    this.playSwipeHint(Array.prototype.slice.call(cards));
  }
};

window.PLPMobileSwipe.init();
